import { useEffect, useState } from "react";
import { Fingerprint, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  canOfferBiometricUnlock, registerPasskey, listPasskeys, deletePasskey,
} from "@/lib/passkeys";

function deviceLabel(c) {
  const ua = c.device_label || "";
  if (/iphone|ipad/i.test(ua)) return "iPhone / iPad";
  if (/android/i.test(ua)) return "Android phone";
  if (/mac/i.test(ua)) return "Mac";
  if (/windows/i.test(ua)) return "Windows PC";
  return "This device";
}

export function BiometricUnlockCard() {
  const [supported] = useState(canOfferBiometricUnlock());
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setCreds(await listPasskeys()); } catch { setCreds([]); }
  };
  useEffect(() => { if (supported) load(); }, [supported]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      toast.success("Biometric unlock is on for this device");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Couldn't set up biometric unlock";
      if (!/cancel/i.test(String(msg))) toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deletePasskey(id);
      const left = (creds || []).filter((c) => c.id !== id);
      setCreds(left);
      if (left.length === 0) localStorage.removeItem("fl_passkey");
      toast.message("Passkey removed");
    } catch {
      toast.error("Couldn't remove that passkey");
    }
  };

  return (
    <div className="hard-card p-5" data-testid="biometric-unlock-card">
      <div className="label-tag mb-1 flex items-center gap-1.5">
        <Fingerprint className="w-3 h-3" /> Fingerprint / Face unlock
      </div>
      <p className="text-sm text-zinc-600 dark:text-zinc-400">
        Sign back in with your fingerprint or face — no password typing on site.
      </p>
      {(creds || []).length > 0 && (
        <div className="mt-3 space-y-2">
          {creds.map((c) => (
            <div key={c.id} className="flex items-center justify-between border border-zinc-200 dark:border-zinc-700 rounded-sm px-3 py-2" data-testid={`passkey-row-${c.id}`}>
              <div className="flex items-center gap-2 text-sm">
                <Fingerprint className="w-4 h-4 text-[#FF5F15]" />
                <span className="font-medium">{deviceLabel(c)}</span>
                <span className="text-[11px] text-zinc-500">
                  added {String(c.created_at || "").slice(0, 10)}
                </span>
              </div>
              <button onClick={() => remove(c.id)} className="text-zinc-400 hover:text-red-600 transition-colors" data-testid={`passkey-remove-${c.id}`} aria-label="Remove passkey">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <Button
        onClick={enable}
        disabled={busy}
        className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider"
        data-testid="biometric-enable-btn"
      >
        <Fingerprint className="w-4 h-4 mr-1.5" />
        {busy ? "Waiting for your device…" : (creds || []).length > 0 ? "Add this device" : "Turn on biometric unlock"}
      </Button>
    </div>
  );
}
